import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { LineChart, BarChart, PieChart, WalletCards, Calculator, Landmark } from 'lucide-react';

interface ServiceCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  delay: number;
}

const ServiceCard: React.FC<ServiceCardProps> = ({ title, description, icon, delay }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ duration: 0.7, delay: delay * 0.1 }}
      className="relative group"
    >
      <div className="relative h-full p-8 bg-white rounded-2xl overflow-hidden transition-all duration-300 hover:translate-y-[-4px] border border-gray-100 hover:border-black/10 hover:shadow-xl">
        
        <div className="relative z-10">
          <div className="inline-flex p-3 rounded-xl bg-gray-50 mb-6 group-hover:bg-black/5 transition-colors">
            {React.cloneElement(icon as React.ReactElement, { 
              size: 24,
              className: "text-black" 
            })}
          </div>
          
          <h3 className="font-sans text-xl font-semibold mb-4 tracking-tight">{title}</h3>
          <p className="text-gray-600 leading-relaxed">{description}</p>
        </div>
        
        <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-black transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
      </div>
    </motion.div>
  );
};

const Services: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const services = [
    {
      title: "Demat Account Management",
      description: "Expert handling of your securities in dematerialized form with advanced portfolio tracking and optimization.",
      icon: <WalletCards size={32} className="text-primary-600" />,
    },
    {
      title: "Portfolio Optimization",
      description: "Data-driven strategies to balance your portfolio for maximum returns while managing risk effectively.",
      icon: <LineChart size={32} className="text-primary-600" />,
    },
    {
      title: "Tax-Efficient Investing",
      description: "Strategic investment approaches designed to minimize tax liability while maximizing growth potential.",
      icon: <Calculator size={32} className="text-primary-600" />,
    },
    {
      title: "Wealth Advisory",
      description: "Personalized guidance from expert financial advisors to help achieve your long-term financial goals.",
      icon: <Landmark size={32} className="text-primary-600" />,
    },
    {
      title: "Market Analysis",
      description: "In-depth research and real-time market analysis to identify emerging opportunities and trends.",
      icon: <BarChart size={32} className="text-primary-600" />,
    },
    {
      title: "Retirement Planning",
      description: "Comprehensive strategies to secure your financial future with reliable income streams post-retirement.",
      icon: <PieChart size={32} className="text-primary-600" />,
    },
  ];

  return (
    <section id="services" className="py-20 bg-gray-50">
      <div className="container">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.7 }}
          className="text-center max-w-3xl mx-auto mb-20"
        >
          <span className="inline-block px-4 py-2 rounded-full bg-gray-900 text-white text-sm font-medium mb-8">
            What We Offer
          </span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-8">
            Our Services
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Comprehensive financial solutions designed to optimize your investments
            and secure your financial future.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
          {services.map((service, index) => (
            <ServiceCard
              key={index}
              title={service.title}
              description={service.description}
              icon={service.icon}
              delay={index + 1}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;